import { z } from 'zod';

/** Locales the project supports. Order = sort order in switchers. PT is canonical/fallback. */
export const LOCALES = ['pt', 'en', 'fr', 'es', 'de'] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'pt';

export const LOCALE_LABEL: Record<Locale, string> = {
  pt: 'Português',
  en: 'English',
  fr: 'Français',
  es: 'Español',
  de: 'Deutsch',
};

/** ISO 639-1 → BCP 47 region we ship with. Used by `Intl.*` formatters. */
export const LOCALE_TAG: Record<Locale, string> = {
  pt: 'pt-PT',
  en: 'en-GB',
  fr: 'fr-FR',
  es: 'es-ES',
  de: 'de-DE',
};

/** Zod schema for a locale value coming off the wire. */
export const localeSchema = z.enum(LOCALES);

/** A translatable string — shape used in every JSONB translation column. */
export const trSchema = z.object({
  pt: z.string(),
  en: z.string(),
  fr: z.string(),
  es: z.string(),
  de: z.string(),
});
export type Tr = z.infer<typeof trSchema>;

/** Read a translatable value with PT fallback. */
export function t(value: Partial<Tr> | null | undefined, locale: Locale): string {
  if (!value) return '';
  return value[locale] ?? value.pt ?? '';
}

/** Strict 5-locale completeness check. Used by the admin "publish" gate. */
export function isFullyTranslated(value: Partial<Tr> | null | undefined): value is Tr {
  if (!value) return false;
  return LOCALES.every((l) => typeof value[l] === 'string' && value[l]!.trim().length > 0);
}
